import React from 'react';
import { AlertCircle } from 'lucide-react';

const IABTaxonomyCard = ({ iabTaxonomy }) => {
  if (!iabTaxonomy) {
    return (
      <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 p-6">
        <h2 className="text-md font-semibold text-gray-900 dark:text-neutral-100 mb-4">
          IAB Content Taxonomy
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-neutral-400 text-sm">No IAB taxonomy classification available</p>
        </div>
      </div>
    );
  }

  const contextualTargeting = iabTaxonomy?.contextual_targeting || {};
  const topics = contextualTargeting.topics || [];
  const genres = contextualTargeting.genre || [];
  const brandSafetyFlag = iabTaxonomy?.brand_safety_flag || 'Unknown';

  // Combine topics and genres for display
  const allClassifications = [
    ...topics.map(t => ({ ...t, type: 'Topic' })),
    ...genres.map(g => ({ ...g, type: 'Genre' }))
  ].sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0));

  // Brand safety color mapping
  const getBrandSafetyColor = (flag) => {
    switch (flag?.toLowerCase()) {
      case 'safe':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700';
      case 'unsafe':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700';
      case 'caution':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
      default:
        return 'bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-neutral-200 border-gray-300 dark:border-neutral-700';
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-gray-900 dark:text-neutral-100">
          IAB Content Taxonomy
        </h2>
        {brandSafetyFlag && (
          <span className={`px-3 py-1 rounded-md text-xs font-medium border ${getBrandSafetyColor(brandSafetyFlag)}`}>
            {brandSafetyFlag}
          </span>
        )}
      </div>

      {allClassifications.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-neutral-300">IAB ID</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-neutral-300">Category Name</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-neutral-300">Contextual Targeting</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-neutral-300">Tier</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-neutral-300">Relevance Score</th>
              </tr>
            </thead>
            <tbody>
              {allClassifications.map((item, index) => (
                <tr 
                  key={index} 
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-900"
                >
                  <td className="py-2 px-3 text-gray-900 dark:text-neutral-100 font-mono text-xs">
                    {item.iab_id || 'N/A'}
                  </td>
                  <td className="py-2 px-3 text-gray-900 dark:text-neutral-100">
                    <div className="flex items-center">
                      {item.category_name || 'N/A'}
                      {item.sub_genre && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-neutral-400">
                          ({item.sub_genre})
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.type === 'Topic' 
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        : 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                    }`}>
                      {item.type}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700 dark:text-neutral-300">
                    {item.tier || 'N/A'}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center">
                      <span className="text-gray-900 dark:text-neutral-100 font-medium">
                        {(item.relevance_score || 0).toFixed(2)}
                      </span>
                      <div className="ml-2 w-16 h-2 bg-gray-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            (item.relevance_score || 0) >= 0.8 
                              ? 'bg-green-500' 
                              : (item.relevance_score || 0) >= 0.5
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${(item.relevance_score || 0) * 100}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8">
          <AlertCircle className="w-8 h-8 text-gray-400 dark:text-neutral-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-neutral-400 text-sm">No classifications found</p>
        </div>
      )}
    </div>
  );
};

export default IABTaxonomyCard;

